import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { MapPin, Package, Truck, CheckCircle2, Download, RotateCw, Star, X, Clock, Upload, AlertCircle, QrCode, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const stages = [
  { key: "processing", label: "Processing", icon: Package },
  { key: "packed", label: "Packed", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const toAbs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [reviewDlg, setReviewDlg] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [okToProceed, setOkToProceed] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  // QR-based payment flow
  const [activeQR, setActiveQR] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  // Delivery guidelines (mandatory before pay)
  const [delContactName, setDelContactName] = useState("");
  const [delContactPhone, setDelContactPhone] = useState("");
  const [delLocation, setDelLocation] = useState("");
  const [delNotes, setDelNotes] = useState("");
  const [delDirty, setDelDirty] = useState(false);
  const [delSaving, setDelSaving] = useState(false);

  useEffect(() => {
    api.get("/payment-qr/active")
      .then(r => setActiveQR(r.data.active ? r.data.qr : null))
      .catch(() => setActiveQR(null));
  }, []);

  const load = () => {
    api.get(`/orders/${id}`).then(r => {
      const o = r.data;
      setOrder(o);
      // Hydrate delivery form from order — falls back to registration data first time
      if (!delDirty) {
        setDelContactName(o.delivery_contact_name || o.customer_name || "");
        setDelContactPhone(o.delivery_contact_phone || o.customer_whatsapp || "");
        setDelLocation(o.delivery_location || o.customer_location || "");
        setDelNotes(o.delivery_notes || "");
      }
    });
    api.get(`/orders/${id}/review-eligibility`).then(r => setEligible(r.data)).catch(() => setEligible(null));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (!order) return <div className="p-20 text-center text-[#8A8A8A]" data-testid="order-loading">Loading order...</div>;

  const stageIdx = stages.findIndex(s => s.key === order.dispatch_status);
  const sc = order.stock_check_status || "not_requested";
  const isPaid = order.payment_status === "paid";
  const availableItems = order.items.filter(it => (it.availability || "pending") !== "unavailable");
  const unavailableItems = order.items.filter(it => it.availability === "unavailable");

  // Admin-applied special discount + other-cost reflection ----------------
  const orderTotal = Number(order.total || 0);
  const otherCost = Number(order.other_cost || 0);
  const persistedGrand = order.grand_total != null ? Number(order.grand_total) : null;
  const hasSpecialDiscount = persistedGrand != null && Number(order.discount_amount || 0) > 0;
  const discountAmount = Number(order.discount_amount || 0);
  const payableTotal = persistedGrand != null ? persistedGrand : orderTotal;
  const discountLabel = order.additional_discount_type === "percent"
    ? `Special Discount (${Number(order.additional_discount_value || 0)}%)`
    : "Special Discount";

  const isDeliveryComplete =
    (delContactName || "").trim().length > 0 &&
    (delContactPhone || "").trim().length >= 6 &&
    (delLocation || "").trim().length > 0;

  const saveDeliveryDetails = async () => {
    setDelSaving(true);
    try {
      await api.patch(`/orders/${id}/delivery-details`, {
        delivery_contact_name: (delContactName || "").trim(),
        delivery_contact_phone: (delContactPhone || "").trim(),
        delivery_location: (delLocation || "").trim(),
        delivery_notes: (delNotes || "").trim(),
      });
      setDelDirty(false);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save delivery details");
      return false;
    } finally {
      setDelSaving(false);
    }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setProofUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProofUrl(data.url);
      toast.success("Screenshot uploaded — you can now submit payment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setProofUploading(false);
    }
  };

  const confirmAndPay = async () => {
    if (!okToProceed && sc !== "buyer_confirmed") { toast.error("Please tick the confirmation checkbox"); return; }
    if (!isDeliveryComplete) {
      toast.error("Please fill Contact name, Contact number and Location under Delivery guidelines");
      return;
    }
    if (!activeQR) { toast.error("Online payment is temporarily unavailable — please contact admin"); return; }
    if (!proofUrl) { toast.error("Please upload the payment confirmation screenshot"); return; }
    setPayBusy(true);
    try {
      const saved = await saveDeliveryDetails();
      if (!saved) { setPayBusy(false); return; }
      if (sc === "replied") {
        await api.patch(`/orders/${id}/buyer-confirm`);
      }
      await api.post(`/orders/${id}/submit-payment-proof`, { proof_url: proofUrl });
      toast.success("Payment submitted — awaiting admin verification");
      setProofUrl("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit payment");
    } finally {
      setPayBusy(false);
    }
  };

  const advance = async () => {
    await api.post(`/orders/${id}/dispatch`);
    load();
  };

  const reorder = async () => {
    const { data } = await api.post(`/orders/${id}/reorder`);
    toast.success("Order duplicated");
    window.location.href = `/orders/${data.id}`;
  };

  const submitReview = async () => {
    if (!reviewForm.comment.trim()) { toast.error("Write a short review"); return; }
    setReviewBusy(true);
    try {
      await api.post("/reviews", {
        order_id: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success("Thanks! Your review is being analyzed by AI...");
      setReviewDlg(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally { setReviewBusy(false); }
  };

  const headline = isPaid
    ? "Order confirmed"
    : sc === "pending" ? "Awaiting stock check"
    : sc === "replied" ? "Review updated cart & pay"
    : sc === "buyer_confirmed" ? "Ready to pay"
    : "Awaiting payment";

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="order-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="label-eyebrow">Order #{order.order_no}</p>
          <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3" data-testid="order-headline">
            {headline}
          </h1>
          <p className="text-[#5C5C5C] mt-4">
            {hasSpecialDiscount ? (
              <>
                <span className="line-through text-[#8A8A8A] mr-2">₹{orderTotal.toFixed(0)}</span>
                Grand Total <span className="font-medium text-[#1E2C5A]" data-testid="order-grand-total">₹{payableTotal.toFixed(0)}</span>
                <span className="ml-2 text-[11px] uppercase tracking-[0.15em] text-[#C9A968]">You save ₹{discountAmount.toFixed(0)}</span>
              </>
            ) : (
              <>Total <span className="font-medium text-[#1A1A1A]">₹{orderTotal.toFixed(0)}</span></>
            )}
            {" "}· {order.items.length} products
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {isPaid && (
            <>
              <a href={`${API}/orders/${order.id}/invoice`} target="_blank" rel="noreferrer" className="btn-outline-ink" data-testid="download-invoice">
                <Download className="w-4 h-4" strokeWidth={1.5} /> Invoice
              </a>
              <a href={`${API}/orders/${order.id}/challan`} target="_blank" rel="noreferrer" className="btn-outline-ink" data-testid="download-challan">
                <Download className="w-4 h-4" strokeWidth={1.5} /> Challan
              </a>
              <button onClick={reorder} className="btn-clay" data-testid="reorder-btn">
                <RotateCw className="w-4 h-4" strokeWidth={1.5} /> Reorder
              </button>
            </>
          )}
          {eligible?.delivered && !eligible?.already_reviewed && (
            <button onClick={() => setReviewDlg(true)} className="btn-outline-ink !border-[#C9A968] !text-[#1E2C5A] hover:!bg-[#C9A968]" data-testid="leave-review-btn">
              <Star className="w-4 h-4" strokeWidth={1.5} /> Leave Review
            </button>
          )}
          {eligible?.already_reviewed && (
            <div className="text-xs text-[#5C5C5C] flex items-center gap-1 self-center" data-testid="already-reviewed">
              <CheckCircle2 className="w-4 h-4 text-[#C9A968]" strokeWidth={1.5} /> Reviewed
            </div>
          )}
        </div>
      </div>

      {/* ============ STOCK CHECK STATES (pre-payment) ============ */}
      {!isPaid && sc === "pending" && (
        <section className="border-l-4 border-[#C9A968] bg-[#FDF9EE] p-8 mb-12" data-testid="sc-pending">
          <div className="flex items-start gap-4">
            <Clock className="w-6 h-6 text-[#C9A968] mt-1" strokeWidth={1.5} />
            <div>
              <p className="label-eyebrow !text-[#8B7A3E]">Step 1 of 2 · Awaiting Admin</p>
              <h3 className="font-editorial text-3xl tracking-editorial mt-2">Your request is with our team</h3>
              <p className="text-sm text-[#5C5C5C] mt-3 max-w-2xl">
                Our admin is checking stock availability for every item in your cart. As soon as they respond (typically within a day),
                you&apos;ll see this order split into <em>Available</em> and <em>Currently Unavailable Inventory</em> with lead times, and
                the option to Pay Online will unlock.
              </p>
              <p className="text-xs text-[#8A8A8A] mt-4">You&apos;ll also receive a WhatsApp update on {order.customer_whatsapp}.</p>
            </div>
          </div>
        </section>
      )}

      {!isPaid && (sc === "replied" || sc === "buyer_confirmed") && (
        <section className="mb-12" data-testid="sc-replied">
          <p className="label-eyebrow !text-[#1E2C5A]">Stock Check Response · from Admin</p>
          <h3 className="font-editorial text-3xl tracking-editorial mt-2 mb-6">Your updated cart</h3>

          {/* Available */}
          <div className="border border-[#1A1A1A]/10 mb-6" data-testid="available-section">
            <div className="bg-[#F0EFEA] px-6 py-3 flex items-center gap-2 border-b border-[#1A1A1A]/10">
              <CheckCircle2 className="w-4 h-4 text-green-700" strokeWidth={1.5} />
              <p className="label-eyebrow !text-[#1A1A1A]">Available now · {availableItems.length}</p>
            </div>
            {availableItems.length === 0
              ? <div className="p-6 text-sm text-[#8A8A8A]">No items are currently available.</div>
              : <div className="divide-y divide-[#1A1A1A]/10">
                  {availableItems.map(it => (
                    <div key={it.product_id} className="px-6 py-4" data-testid={`available-${it.our_code}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{it.name}</p>
                          <p className="text-xs text-[#8A8A8A]">Code {it.our_code} · {it.size} · {it.quantity_boxes} boxes</p>
                        </div>
                        <p className="font-medium">₹{it.subtotal.toFixed(0)}</p>
                      </div>
                      {it.admin_remark && (
                        <p className="text-[11px] text-[#8B7A3E] bg-[#FDF9EE] border-l-2 border-[#C9A968] px-3 py-1.5 mt-2 italic" data-testid={`available-remark-${it.our_code}`}>
                          <span className="not-italic uppercase tracking-[0.15em] text-[9px] mr-2 text-[#1E2C5A]">Note</span>
                          {it.admin_remark}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
            }
          </div>

          {/* Unavailable */}
          {unavailableItems.length > 0 && (
            <div className="border border-[#C9A968]/50 mb-6" data-testid="unavailable-section">
              <div className="bg-[#FDF9EE] px-6 py-3 flex items-center gap-2 border-b border-[#C9A968]/40">
                <AlertCircle className="w-4 h-4 text-[#8B7A3E]" strokeWidth={1.5} />
                <p className="label-eyebrow !text-[#8B7A3E]">Currently Unavailable Inventory · {unavailableItems.length}</p>
              </div>
              <div className="divide-y divide-[#C9A968]/30">
                {unavailableItems.map(it => (
                  <div key={it.product_id} className="px-6 py-4" data-testid={`unavailable-${it.our_code}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{it.name}</p>
                        <p className="text-xs text-[#8A8A8A]">Code {it.our_code} · {it.size} · {it.quantity_boxes} boxes</p>
                        <p className="text-xs text-[#8B7A3E] mt-1">
                          Estimated lead time: <span className="font-medium">{it.lead_time_days || "?"} days</span>
                        </p>
                      </div>
                      <p className="font-medium text-[#5C5C5C]">₹{it.subtotal.toFixed(0)}</p>
                    </div>
                    {it.admin_remark && (
                      <p className="text-[11px] text-[#8B7A3E] bg-white border-l-2 border-[#C9A968] px-3 py-1.5 mt-2 italic" data-testid={`unavailable-remark-${it.our_code}`}>
                        <span className="not-italic uppercase tracking-[0.15em] text-[9px] mr-2 text-[#1E2C5A]">Note</span>
                        {it.admin_remark}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 bg-[#FDF9EE] border-t border-[#C9A968]/30 text-xs text-[#8B7A3E]">
                These items are included in this payment and will be shipped as soon as they arrive. You&apos;ll be able to track them under <strong>Yet to Receive</strong>.
              </div>
            </div>
          )}

          {order.stock_check_note && (
            <div className="bg-[#F0EFEA] p-4 mb-6 text-sm text-[#5C5C5C]" data-testid="stock-check-note">
              <p className="label-eyebrow mb-1">Admin note</p>
              {order.stock_check_note}
            </div>
          )}

          {/* Price summary — always visible; highlights any admin discount */}
          <div className="border border-[#1A1A1A]/15 bg-white p-6 mb-6" data-testid="checkout-price-summary">
            <p className="label-eyebrow mb-3">Price Summary</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5C5C5C]">Cart Total (GST incl.)</span>
                <span className={hasSpecialDiscount ? "text-[#8A8A8A] line-through" : "font-medium"} data-testid="summary-cart-total">
                  ₹{orderTotal.toFixed(2)}
                </span>
              </div>
              {hasSpecialDiscount && (
                <div className="flex justify-between text-[#C9A968]" data-testid="summary-discount-row">
                  <span>
                    {discountLabel}
                    {order.additional_discount_reason && (
                      <span className="text-[11px] text-[#8A8A8A] ml-2 italic">· {order.additional_discount_reason}</span>
                    )}
                  </span>
                  <span data-testid="summary-discount-amount">− ₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              {otherCost > 0 && (
                <div className="flex justify-between text-[#1E2C5A]" data-testid="summary-other-cost-row">
                  <span>
                    Other Cost
                    {order.other_cost_reason && (
                      <span className="text-[11px] text-[#8A8A8A] ml-2 italic">· {order.other_cost_reason}</span>
                    )}
                  </span>
                  <span data-testid="summary-other-cost-amount">+ ₹{otherCost.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-[#1A1A1A]/10 pt-2 flex justify-between font-editorial text-xl text-[#1E2C5A]">
                <span>Grand Total (Payable)</span>
                <span data-testid="summary-grand-total">₹{payableTotal.toFixed(2)}</span>
              </div>
              {hasSpecialDiscount && (
                <p className="text-[11px] text-[#8B7A3E] bg-[#FDF9EE] border border-[#C9A968]/40 px-3 py-2 mt-2" data-testid="summary-discount-note">
                  A special discount of ₹{discountAmount.toFixed(2)} has been applied by our team. This is reflected in the Pay Online amount below.
                </p>
              )}
            </div>
          </div>


          {/* To-pay basis banner + Delivery guidelines */}
          <div
            className="mb-6 border border-[#C9A968]/50 bg-[#FDF9EE] p-4 text-center"
            data-testid="topay-banner"
          >
            <p className="font-editorial text-lg sm:text-xl text-[#1E2C5A] tracking-wide">
              All deliveries are done on <span className="text-[#8B5E00] uppercase">to-pay basis</span>
            </p>
            <p className="text-[11px] text-[#8B7A3E] mt-1">Freight charges are paid directly to the logistics partner at the time of delivery.</p>
          </div>

          <div className="mb-6 border border-[#1A1A1A]/15 bg-white p-6" data-testid="delivery-guidelines">
            <p className="label-eyebrow">Step 1 · Mandatory</p>
            <h3 className="font-editorial text-2xl mt-1 mb-4">Delivery Guidelines</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C]">
                  Contact name <span className="text-red-700">*</span>
                </label>
                <input
                  type="text"
                  value={delContactName}
                  onChange={(e) => { setDelContactName(e.target.value); setDelDirty(true); }}
                  placeholder="Person receiving the delivery"
                  className="w-full h-10 border border-[#1A1A1A]/20 px-3 text-sm mt-1 focus:outline-none focus:border-[#1E2C5A]"
                  data-testid="delivery-contact-name"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C]">
                  Contact number <span className="text-red-700">*</span>
                </label>
                <input
                  type="tel"
                  value={delContactPhone}
                  onChange={(e) => { setDelContactPhone(e.target.value); setDelDirty(true); }}
                  placeholder="10-digit mobile"
                  className="w-full h-10 border border-[#1A1A1A]/20 px-3 text-sm mt-1 focus:outline-none focus:border-[#1E2C5A]"
                  data-testid="delivery-contact-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C]">
                  Location <span className="text-red-700">*</span> <span className="normal-case tracking-normal text-[#8A8A8A]">— preferred: paste a Google Maps link</span>
                </label>
                <input
                  type="text"
                  value={delLocation}
                  onChange={(e) => { setDelLocation(e.target.value); setDelDirty(true); }}
                  placeholder="https://maps.app.goo.gl/... or full address with landmark"
                  className="w-full h-10 border border-[#1A1A1A]/20 px-3 text-sm mt-1 focus:outline-none focus:border-[#1E2C5A]"
                  data-testid="delivery-location"
                />
                <a
                  href="https://www.google.com/maps"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-[#1E2C5A] hover:underline mt-1 inline-flex items-center gap-1"
                  data-testid="delivery-open-gmaps"
                >
                  <MapPin className="w-3 h-3" strokeWidth={1.5} /> Open Google Maps to grab the link
                </a>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C]">
                  Notes <span className="normal-case tracking-normal text-[#8A8A8A]">— optional</span>
                </label>
                <textarea
                  rows={3}
                  value={delNotes}
                  onChange={(e) => { setDelNotes(e.target.value); setDelDirty(true); }}
                  placeholder="e.g. Call 30 min before delivery. Access from back gate. Lift not available — ground floor only."
                  className="w-full border border-[#1A1A1A]/20 p-3 text-sm mt-1 focus:outline-none focus:border-[#1E2C5A] resize-none"
                  data-testid="delivery-notes"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1A1A1A]/10">
              <p className="text-[11px] text-[#8A8A8A]">
                {isDeliveryComplete
                  ? (delDirty ? "You have unsaved changes." : "Saved — you can proceed to payment.")
                  : "Please fill all fields marked with *"}
              </p>
              <button
                type="button"
                onClick={saveDeliveryDetails}
                disabled={delSaving || !isDeliveryComplete || !delDirty}
                className="text-xs uppercase tracking-[0.2em] border border-[#1E2C5A] text-[#1E2C5A] hover:bg-[#1E2C5A] hover:text-white px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="save-delivery-details"
              >
                {delSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {/* Confirmation checkbox + Pay */}
          <div className="border border-[#1A1A1A]/20 p-6 bg-white">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="confirm-updated-cart">
              <input
                type="checkbox"
                checked={okToProceed || sc === "buyer_confirmed"}
                disabled={sc === "buyer_confirmed"}
                onChange={(e) => setOkToProceed(e.target.checked)}
                className="mt-1 accent-[#1E2C5A] w-4 h-4"
              />
              <span className="text-sm">
                I am okay with the updated cart and want to proceed with a single payment for all <span className="font-medium">{order.items.length}</span> item(s).
                {unavailableItems.length > 0 && <> The {unavailableItems.length} unavailable item(s) will be reserved and shipped as soon as they arrive — I can track them under <em>Yet to Receive</em>.</>}
              </span>
            </label>

            {/* QR-based payment flow */}
            <div className="mt-6 border-t border-[#1A1A1A]/10 pt-6" data-testid="qr-payment-section">
              <div className="flex items-center gap-2 mb-3">
                <QrCode className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
                <p className="label-eyebrow !text-[#1A1A1A]">Step 2 · Scan &amp; Pay</p>
              </div>
              {order.payment_verification_status === "awaiting_verification" ? (
                <div className="bg-[#FDF9EE] border border-[#C9A968]/40 p-6 text-center" data-testid="awaiting-verification-card">
                  <Clock className="w-8 h-8 text-[#C9A968] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="font-editorial text-xl text-[#1E2C5A]">Payment proof submitted</p>
                  <p className="text-sm text-[#5C5C5C] mt-2">Our admin is verifying your payment. You&apos;ll get a WhatsApp / email once approved.</p>
                  {order.payment_proof_url && (
                    <a href={toAbs(order.payment_proof_url)} target="_blank" rel="noreferrer" className="text-xs text-[#1E2C5A] hover:underline mt-3 inline-block" data-testid="view-submitted-proof">
                      View submitted screenshot →
                    </a>
                  )}
                </div>
              ) : order.payment_verification_status === "rejected" ? (
                <div className="bg-red-50 border border-red-200 p-4 text-sm mb-4" data-testid="proof-rejected-banner">
                  <p className="font-medium text-red-800">Payment proof rejected</p>
                  <p className="text-red-700 mt-1">{order.payment_verification_note || "Please re-upload a clearer payment screenshot."}</p>
                </div>
              ) : null}

              {activeQR && order.payment_verification_status !== "awaiting_verification" && (
                <>
                  <div className="mb-4 border-2 border-[#C9A968] bg-[#FDF9EE] p-4" data-testid="qr-payment-instructions">
                    <p className="font-editorial text-xl md:text-2xl text-[#1E2C5A] leading-tight" data-testid="qr-payment-instruction-headline">
                      Please send <span className="text-[#C9A968]">₹1</span> first, confirm with us offline, then send the balance amount.
                    </p>
                    <p className="text-[11px] text-[#8B7A3E] mt-2 uppercase tracking-[0.15em]">
                      This one-step check protects your full payment against wrong-UPI errors.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div className="border border-[#1A1A1A]/15 bg-white p-4 flex flex-col items-center" data-testid="active-qr-card">
                      <img
                        src={toAbs(activeQR.image_url)}
                        alt="Payment QR"
                        className="w-full max-w-[280px] aspect-square object-contain"
                        data-testid="active-qr-image"
                      />
                      {activeQR.label && <p className="text-xs text-[#5C5C5C] mt-3 text-center">{activeQR.label}</p>}
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mt-2">Scan with any UPI app</p>
                    </div>
                    <div>
                      <p className="label-eyebrow mb-2">Step 3 · Upload Screenshot</p>
                      <p className="text-xs text-[#5C5C5C] mb-3">After paying, upload the confirmation screenshot below.</p>
                      <div className="bg-[#FDF9EE] border border-[#C9A968]/40 p-3 mb-3 text-[11px] text-[#8B7A3E] leading-relaxed" data-testid="proof-guidelines">
                        <p className="uppercase tracking-[0.15em] text-[10px] font-medium text-[#1E2C5A] mb-1.5">Please make sure the screenshot clearly shows</p>
                        <ul className="space-y-0.5 pl-3 list-disc">
                          <li><strong>The exact amount paid</strong> — must be legible (₹{payableTotal.toFixed(2)})</li>
                          <li>Transaction ID / UTR reference</li>
                          <li>Date &amp; time of payment</li>
                          <li>Payment status: <em>Success / Completed</em></li>
                        </ul>
                        <p className="mt-1.5 text-[10px] italic">Blurry or cropped screenshots may be rejected and slow down your order.</p>
                      </div>
                      <label className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] cursor-pointer transition-colors ${proofUploading ? "bg-[#8A8A8A] text-white" : proofUrl ? "bg-green-700 text-white" : "bg-[#1E2C5A] text-white hover:bg-[#141F45]"}`} data-testid="proof-upload-label">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {proofUploading ? "Uploading..." : proofUrl ? "Screenshot Uploaded — Replace" : "Upload Screenshot"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={proofUploading}
                          onChange={(e) => { uploadProof(e.target.files?.[0]); e.target.value = ""; }}
                          data-testid="proof-upload-input"
                        />
                      </label>
                      {proofUrl && (
                        <div className="mt-3 border border-[#C9A968]/40 p-2 inline-block" data-testid="proof-preview">
                          <img src={toAbs(proofUrl)} alt="proof preview" className="max-w-[200px] max-h-[200px] object-contain" />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={confirmAndPay}
              disabled={payBusy || (!okToProceed && sc !== "buyer_confirmed") || order.items.length === 0 || !isDeliveryComplete || !proofUrl || !activeQR || order.payment_verification_status === "awaiting_verification"}
              className="btn-clay mt-6 w-full disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="submit-payment-btn"
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
              {payBusy ? "Submitting…" : order.payment_verification_status === "awaiting_verification"
                ? "Awaiting admin verification…"
                : `Submit Payment · ₹${payableTotal.toFixed(0)}`}
            </button>
            {!activeQR && (
              <p className="text-[11px] text-red-700 mt-2 text-center" data-testid="qr-unavailable">
                Online payment is temporarily unavailable. Please contact admin.
              </p>
            )}
            {activeQR && !proofUrl && order.payment_verification_status !== "awaiting_verification" && (
              <p className="text-[11px] text-[#8B7A3E] mt-2 text-center" data-testid="proof-required-warn">
                Upload your payment screenshot above to enable Submit Payment.
              </p>
            )}
            {!isDeliveryComplete && (
              <p className="text-[11px] text-red-700 mt-2 text-center" data-testid="pay-delivery-warn">
                Fill the Delivery Guidelines above to enable payment.
              </p>
            )}
            <p className="text-[10px] uppercase tracking-wider text-[#8A8A8A] mt-3 text-center">UPI · Scan the QR above with any UPI app</p>
          </div>
        </section>
      )}

      {reviewDlg && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-[#FAF9F6] max-w-lg w-full p-10 relative fade-up">
            <button onClick={() => setReviewDlg(false)} className="absolute top-4 right-4 text-[#5C5C5C]" data-testid="review-close">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="label-eyebrow">Share Your Experience</p>
            <h2 className="font-editorial text-3xl tracking-editorial mt-3 mb-6">How was it?</h2>
            <div className="flex items-center gap-2 mb-6" data-testid="review-stars">
              {[1,2,3,4,5].map(i => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setReviewForm(f => ({ ...f, rating: i }))}
                  className={`transition-colors ${i <= reviewForm.rating ? "text-[#C9A968]" : "text-[#E5E5E5]"}`}
                  data-testid={`review-star-${i}`}
                >
                  <Star className={`w-8 h-8 ${i <= reviewForm.rating ? "fill-[#C9A968]" : ""}`} strokeWidth={1.5} />
                </button>
              ))}
            </div>
            <textarea
              rows={5}
              className="w-full bg-transparent border-b border-[#d6d3ce] py-3 outline-none focus:border-[#1E2C5A] text-sm resize-none"
              placeholder="What impressed you? Tiles, delivery, service..."
              value={reviewForm.comment}
              onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
              data-testid="review-comment"
            />
            <button onClick={submitReview} disabled={reviewBusy} className="btn-clay w-full mt-6" data-testid="review-submit">
              {reviewBusy ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      )}

      {/* Dispatch progress — only after payment */}
      {isPaid && (
        <section className="border border-[#1A1A1A]/10 p-8 mb-12" data-testid="dispatch-panel">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
              <p className="label-eyebrow !text-[#1A1A1A]">Live Dispatch</p>
            </div>
            <button onClick={advance} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]" data-testid="advance-dispatch">Advance stage →</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {stages.map((s, i) => {
              const active = i <= stageIdx;
              const Icon = s.icon;
              return (
                <div key={s.key} className={`border-t-2 pt-4 ${active ? "border-[#1E2C5A]" : "border-[#E5E5E5]"}`} data-testid={`stage-${s.key}`}>
                  <Icon className={`w-5 h-5 ${active ? "text-[#1E2C5A]" : "text-[#8A8A8A]"}`} strokeWidth={1.5} />
                  <p className={`mt-2 text-sm ${active ? "text-[#1A1A1A] font-medium" : "text-[#8A8A8A]"}`}>{s.label}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8 aspect-[16/6] bg-[#F0EFEA] relative overflow-hidden" data-testid="dispatch-map">
            <iframe
              title="dispatch map"
              className="w-full h-full border-0"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${order.dispatch_lng - 0.15}%2C${order.dispatch_lat - 0.08}%2C${order.delivery_lng + 0.15}%2C${order.delivery_lat + 0.08}&layer=mapnik&marker=${order.dispatch_lat}%2C${order.dispatch_lng}`}
            />
            <div className="absolute bottom-3 left-3 bg-[#FAF9F6]/95 px-3 py-2 text-xs">
              Truck: {order.dispatch_lat.toFixed(4)}, {order.dispatch_lng.toFixed(4)} → Delivery: {order.delivery_lat.toFixed(4)}, {order.delivery_lng.toFixed(4)}
            </div>
          </div>
        </section>
      )}

      {/* Yet to Receive — paid backorder items on this order */}
      {isPaid && unavailableItems.length > 0 && (
        <section className="mb-12 border-l-4 border-[#C9A968] bg-[#FDF9EE] p-8" data-testid="yet-to-receive-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="label-eyebrow !text-[#8B7A3E]">Backorder · Reserved</p>
              <h3 className="font-editorial text-3xl tracking-editorial mt-2">Yet to Receive</h3>
              <p className="text-xs text-[#8A8A8A] mt-2">These items are paid and reserved. We&apos;ll update the status as they progress from procurement to your doorstep.</p>
            </div>
            <Link to="/yet-to-receive" className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]">View all →</Link>
          </div>
          <div className="space-y-4">
            {unavailableItems.map(it => {
              const ps = it.procurement_status || "awaiting_procurement";
              const stagesP = [
                { key: "inventory_ready", label: "Inventory Ready" },
                { key: "ready_for_dispatch", label: "Ready for Dispatch" },
                { key: "dispatched", label: "Dispatched" },
                { key: "delivered", label: "Delivered" },
              ];
              const preReady = ["awaiting_procurement", "ordered", "picked", "shipped"].includes(ps);
              const idx = preReady ? -1 : stagesP.findIndex(s => s.key === ps);
              return (
                <div key={it.product_id} className="bg-white p-5 border border-[#C9A968]/30" data-testid={`backorder-${it.our_code}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-medium">{it.name}</p>
                      <p className="text-xs text-[#8A8A8A] mt-1">Code {it.our_code} · {it.size} · {it.quantity_boxes} boxes</p>
                    </div>
                    {ps === "inventory_ready" && (
                      <span className="text-xs uppercase tracking-[0.15em] text-green-800 bg-green-50 px-3 py-1 border border-green-700/30" data-testid={`ready-badge-${it.our_code}`}>Goods are Ready</span>
                    )}
                    {preReady && (
                      <span className="text-xs uppercase tracking-[0.15em] text-[#8B7A3E]">Being arranged</span>
                    )}
                  </div>
                  {preReady ? (
                    <p className="text-xs text-[#8A8A8A]">Our team is arranging this with the supplier. WhatsApp update coming when ready.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-1">
                      {stagesP.map((s, i) => (
                        <div key={s.key} className={`border-t-2 pt-2 ${i <= idx ? "border-[#C9A968]" : "border-[#E5E5E5]"}`} title={s.label}>
                          <p className={`text-[10px] uppercase tracking-wide ${i <= idx ? "text-[#8B7A3E] font-medium" : "text-[#8A8A8A]"}`}>{s.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Items — always visible when no stock-check reply yet, otherwise redundant */}
      {(sc === "pending" || sc === "not_requested" || isPaid) && (
        <section className="mb-12">
          <h3 className="font-editorial text-2xl mb-6">Items</h3>
          <div className="divide-y divide-[#1A1A1A]/10">
            {order.items.map(it => (
              <div key={it.product_id} className="py-5 flex justify-between items-center" data-testid={`order-item-${it.our_code}`}>
                <div>
                  <p className="font-medium">{it.name}</p>
                  <p className="text-xs text-[#8A8A8A]">Code {it.our_code} · {it.size} · {it.quantity_boxes} boxes ({it.packing}/box)</p>
                </div>
                <p className="font-medium">₹{it.subtotal.toFixed(0)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tile layers */}
      {order.tile_laying_service && order.top_tile_layers?.length > 0 && (
        <section className="mb-12 bg-[#F0EFEA] p-8" data-testid="order-layers">
          <p className="label-eyebrow">Sent to your WhatsApp</p>
          <h3 className="font-editorial text-3xl mt-2 mb-6">Top 5 Tile Layers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {order.top_tile_layers.map((l, i) => (
              <div key={l.id || l.contact || `layer-${i}`} className="bg-white p-5 border border-[#1A1A1A]/10">
                <p className="font-medium">{l.name}</p>
                <p className="text-sm text-[#1E2C5A] mt-1">{l.contact}</p>
                <p className="text-xs text-[#8A8A8A] mt-2">{l.experience_years} years experience</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Live tile flag */}
      {order.see_live_tile && (
        <section className="mb-12 border-l-4 border-[#1E2C5A] pl-6 py-2" data-testid="see-live-flag">
          <p className="label-eyebrow">Premium</p>
          <p className="font-editorial text-2xl mt-1">See Live Tile requested — our team will call you to schedule a showroom viewing.</p>
        </section>
      )}

      <p className="text-xs text-[#8A8A8A]"><Link to="/orders" className="link-slide">← All orders</Link></p>
    </div>
  );
}
